import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * The one layout every record page uses: header and facts on top, a work
 * column (brief, type panels, activity) and a reference rail. Two columns from
 * xl; below that the rail becomes a two-up grid under the work column.
 *
 * `notice` holds status banners (on hold, closed) under the facts. A page that
 * keeps tabs (only Client, per the spec) passes its tab list as `tabs` and, on
 * any tab but Overview, that tab's content as `view`, which then replaces the
 * work column and rail.
 */
export function RecordFrame({ header, facts, notice, tabs, view, work, rail }: {
  header: ReactNode; facts?: ReactNode; notice?: ReactNode; tabs?: ReactNode; view?: ReactNode; work: ReactNode; rail: ReactNode;
}) {
  return (
    <div className="space-y-3.5">
      {header}
      {facts}
      {notice}
      {tabs}
      {view ?? (
        <div className="grid items-start gap-3.5 xl:grid-cols-[minmax(0,1fr)_340px]">
          <div className="grid min-w-0 gap-3.5">{work}</div>
          <aside aria-label="Reference" className="grid min-w-0 items-start gap-3.5 sm:grid-cols-2 xl:grid-cols-1">{rail}</aside>
        </div>
      )}
    </div>
  );
}

/** A bordered section with the small uppercase heading every panel shares. */
export function RecordPanel({ title, count, action, id, className, children }: {
  title?: ReactNode; count?: number | null; action?: ReactNode; id?: string; className?: string; children: ReactNode;
}) {
  return (
    <section id={id} className={cn("min-w-0 rounded-lg border bg-card px-3.5 pb-3.5 pt-3", className)}>
      {(title || action) && (
        <div className="mb-2 flex min-h-[22px] items-center justify-between gap-2">
          <h3 className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {title}
            {count != null && <span className="font-medium normal-case tracking-normal tabular-nums">{count}</span>}
          </h3>
          {action}
        </div>
      )}
      {children}
    </section>
  );
}
